import { AlertTriangle, CheckCircle2, UserRound } from 'lucide-react';
import { Link } from 'react-router';
import { usePhotoAgent } from '@/api/hooks';
import { LIBELLE_STATUT_DOSSIER_HRMS, type AnomalieDossierHrms, type BlocHrms, type StatutDossierHrms, type StatutPhotoHrms } from '@/api/types';
import { Kpi } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const VARIANTE_STATUT_DOSSIER: Record<StatutDossierHrms, 'default' | 'secondary' | 'destructive' | 'outline'> = { IMPORTE: 'secondary', A_REGULARISER: 'destructive', CONTROLE: 'outline', VALIDE: 'default' };

export const StatutDossierHrmsBadge = ({ statut }: { statut: StatutDossierHrms }) => (
  <Badge variant={VARIANTE_STATUT_DOSSIER[statut]} className={cn(statut === 'VALIDE' && 'border-emerald-300 bg-emerald-50 text-emerald-800', statut === 'CONTROLE' && 'border-sky-300 bg-sky-50 text-sky-800')}>
    {LIBELLE_STATUT_DOSSIER_HRMS[statut]}
  </Badge>
);

/** Photo de l'agent (lue avec la session) ; initiales en attente ou à défaut de photo. */
export function PhotoAgent({ agentId, version, nom, prenom, statut, taille = 'md', className }: { agentId: string; version: string | null | undefined; nom: string; prenom: string; statut?: StatutPhotoHrms | null; taille?: 'sm' | 'md' | 'lg'; className?: string }) {
  const { data: url } = usePhotoAgent(agentId, version);
  const dim = taille === 'lg' ? 'size-32 text-3xl' : taille === 'md' ? 'size-16 text-xl' : 'size-9 text-xs';
  const initiales = `${prenom.charAt(0)}${nom.charAt(0)}`.toUpperCase();
  return (
    <div className={cn('relative shrink-0', className)}>
      {url ? (
        <img src={url} alt={`Photo de ${prenom} ${nom}`} className={cn(dim, 'rounded-full border object-cover')} data-testid="photo-agent" />
      ) : (
        <div className={cn(dim, 'flex items-center justify-center rounded-full border bg-muted font-semibold text-muted-foreground')} aria-label="Aucune photo" data-testid="photo-agent-absente">
          {initiales || <UserRound className="size-5" />}
        </div>
      )}
      {url && statut === 'A_VALIDER' && taille !== 'sm' && (
        <Badge variant="outline" className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap border-amber-300 bg-amber-50 text-[10px] text-amber-800">
          à valider
        </Badge>
      )}
    </div>
  );
}

/** Anomalies d'un dossier : bloquantes d'abord, puis à régulariser. */
export function AnomaliesDossier({ anomalies }: { anomalies: AnomalieDossierHrms[] }) {
  if (!anomalies.length)
    return (
      <p className="flex items-center gap-2 text-sm text-emerald-700">
        <CheckCircle2 className="size-4" /> Aucune anomalie : le dossier est complet.
      </p>
    );
  const bloquante = (a: AnomalieDossierHrms) => a.message.endsWith('(bloquante)');
  const triees = [...anomalies].sort((a, b) => Number(bloquante(b)) - Number(bloquante(a)));
  return (
    <ul className="space-y-1 text-sm" data-testid="anomalies-dossier">
      {triees.map((a) => (
        <li key={a.code} className={cn('flex items-start gap-2', bloquante(a) ? 'text-destructive' : 'text-amber-800')}>
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>
            {a.message.replace(' (bloquante)', '')}
            {bloquante(a) && <Badge variant="destructive" className="ml-2 text-[10px]">bloquante</Badge>}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Bloc du tableau de bord : son dossier pour tout agent ; file de régularisation pour les RH. */
export function BlocHrmsTableau({ bloc }: { bloc: BlocHrms }) {
  const rh = bloc.rh;
  return (
    <Card data-testid="bloc-hrms">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          Mon dossier HRMS
          <span className="flex flex-wrap gap-2">
            <Button asChild size="sm" variant="outline">
              <Link to="/rh/mon-dossier">Ouvrir mon dossier</Link>
            </Button>
            {rh && (
              <Button asChild size="sm" variant="outline">
                <Link to="/rh/hrms">Référentiel HRMS</Link>
              </Button>
            )}
          </span>
        </CardTitle>
        <CardDescription>Dossier unique de l'agent (identité, affectation, contrat, situation sociale, paiement, photo) tenu par les Ressources humaines ; demandez une correction depuis votre dossier.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/rh/mon-dossier">
          <Kpi label="Mon dossier" valeur={bloc.mien.statut ? LIBELLE_STATUT_DOSSIER_HRMS[bloc.mien.statut] : 'à constituer'} detail={`${bloc.mien.anomalies} point(s) à régulariser · photo ${bloc.mien.photo === 'VALIDEE' ? 'validée' : bloc.mien.photo === 'A_VALIDER' ? 'à valider' : 'absente'}`} accent={bloc.mien.statut === 'VALIDE' ? 'ok' : bloc.mien.anomalies > 0 ? 'alerte' : undefined} />
        </Link>
        <Link to="/rh/mon-dossier?onglet=corrections">
          <Kpi label="Mes demandes de correction" valeur={bloc.mien.correctionsOuvertes} detail="en attente des Ressources humaines" />
        </Link>
        {rh && (
          <>
            <Link to="/rh/hrms?statut=A_REGULARISER">
              <Kpi label="Dossiers à régulariser" valeur={rh.aRegulariser} detail={`${rh.dossiers} dossier(s) · ${rh.controles} contrôlé(s) · ${rh.valides} validé(s)`} accent={rh.aRegulariser > 0 ? 'alerte' : 'ok'} />
            </Link>
            <Link to="/rh/hrms?onglet=a-traiter">
              <Kpi label="À traiter (RH)" valeur={rh.corrections + rh.paiements + rh.photos + rh.charges} detail={`${rh.corrections} correction(s) · ${rh.paiements} paiement(s)${rh.validerPaiements ? ' à valider' : ''} · ${rh.photos} photo(s) · ${rh.charges} personne(s) à charge`} accent={rh.corrections + rh.paiements + rh.photos + rh.charges > 0 ? 'alerte' : undefined} />
            </Link>
          </>
        )}
      </CardContent>
    </Card>
  );
}
